// src/features/invoices/components/OffeneZahlungseingaenge.tsx
//
// Klärungsliste im Zahlungsabgleich (XTAX GmbH & Co. KG, 05.11.2026):
// Zahlungseingänge aus dem EBICS-Abruf, die keiner Rechnung automatisch
// zugeordnet werden konnten. Je Eingang lässt sich eine offene Rechnung
// auswählen und zuordnen (die Rechnung wird mit Buchungstag, Betrag und
// Bankreferenz als bezahlt markiert) oder der Eingang bewusst ignorieren.
// Die Liste erscheint nur, wenn es offene Eingänge gibt.

import React, { useState, useEffect, useCallback } from 'react';
import { Loader, Link2, EyeOff } from 'lucide-react';
import { useAuth } from '../../auth';
import { useDialog } from '../../../shared/components/DialogProvider';
import { bankUmsaetzeService, OffenerZahlungseingang } from '../../../services/payment/bankUmsaetzeService';
import { OpenInvoiceListItem } from '../../../services/payment/paymentReconciliationService';

interface Props {
  // Offene Rechnungen aus dem Zahlungsabgleich, Grundlage der Auswahl
  offeneRechnungen: OpenInvoiceListItem[];
  // Wird nach jeder Zuordnung aufgerufen, damit Listen und Mahnvorschläge neu laden
  onGeaendert: () => Promise<void>;
  // Ändert sich der Wert, lädt die Liste neu (z. B. nach einem Abruf)
  aktualisierung?: number;
}

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

const formatDate = (dateStr: string | null): string =>
  dateStr ? new Date(dateStr).toLocaleDateString('de-DE') : '-';

const gleicherBetrag = (a: number, b: number): boolean => Math.abs(a - b) < 0.005;

export const OffeneZahlungseingaenge: React.FC<Props> = ({ offeneRechnungen, onGeaendert, aktualisierung }) => {
  const { user } = useAuth();
  const { confirm: dialogConfirm, notify: dialogNotify } = useDialog();

  const [umsaetze, setUmsaetze] = useState<OffenerZahlungseingang[]>([]);
  const [geladen, setGeladen] = useState(false);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [auswahl, setAuswahl] = useState<Record<string, string>>({});
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);

  const lade = useCallback(async () => {
    const ergebnis = await bankUmsaetzeService.ladeOffene();
    if (ergebnis.success) {
      setUmsaetze(ergebnis.umsaetze);
      setLadeFehler(null);
    } else {
      setLadeFehler(ergebnis.error || 'Zahlungseingänge konnten nicht geladen werden');
    }
    setGeladen(true);
  }, []);

  useEffect(() => {
    lade();
  }, [lade, aktualisierung]);

  // Auswahl: Rechnungen mit gleichem Betrag zuerst, danach nach Rechnungsnummer
  const optionenFuer = (u: OffenerZahlungseingang): OpenInvoiceListItem[] =>
    offeneRechnungen.slice().sort((a, b) => {
      const ga = gleicherBetrag(a.brutto_betrag, u.betrag) ? 0 : 1;
      const gb = gleicherBetrag(b.brutto_betrag, u.betrag) ? 0 : 1;
      if (ga !== gb) return ga - gb;
      return a.rechnungsnummer.localeCompare(b.rechnungsnummer);
    });

  const handleZuordnen = async (u: OffenerZahlungseingang) => {
    if (!user?.email) return;
    const invoiceId = auswahl[u.id];
    const rechnung = offeneRechnungen.find(r => r.id === invoiceId);
    if (!rechnung) return;

    const abweichung = !gleicherBetrag(rechnung.brutto_betrag, u.betrag);
    const bestaetigt = await dialogConfirm({
      titel: 'Zahlungseingang zuordnen',
      text: abweichung
        ? `Der Betrag weicht ab: Zahlungseingang ${formatCurrency(u.betrag)}, Rechnung ${rechnung.rechnungsnummer} ${formatCurrency(rechnung.brutto_betrag)}.\n\nRechnung trotzdem als bezahlt markieren?`
        : `Rechnung ${rechnung.rechnungsnummer} (${rechnung.empfaenger_firma}) mit diesem Zahlungseingang als bezahlt markieren?`,
      bestaetigenLabel: 'Zuordnen',
      variante: abweichung ? 'gefahr' : undefined
    });
    if (!bestaetigt) return;

    setBeschaeftigt(u.id);
    try {
      const ergebnis = await bankUmsaetzeService.ordneZu(u.id, rechnung.id, user.email);
      if (!ergebnis.success) {
        await dialogNotify({ titel: 'Zuordnung fehlgeschlagen', text: ergebnis.error || 'Unbekannter Fehler', variante: 'gefahr' });
      }
      await lade();
      await onGeaendert();
    } finally {
      setBeschaeftigt(null);
    }
  };

  const handleIgnorieren = async (u: OffenerZahlungseingang) => {
    if (!user?.email) return;
    const bestaetigt = await dialogConfirm({
      titel: 'Zahlungseingang ignorieren',
      text: `Den Zahlungseingang über ${formatCurrency(u.betrag)} vom ${formatDate(u.buchungstag)} keiner Rechnung zuordnen? Er verschwindet dann aus dieser Liste.`,
      bestaetigenLabel: 'Ignorieren'
    });
    if (!bestaetigt) return;

    setBeschaeftigt(u.id);
    try {
      const ergebnis = await bankUmsaetzeService.ignoriere(u.id, user.email);
      if (!ergebnis.success) {
        await dialogNotify({ titel: 'Nicht gespeichert', text: ergebnis.error || 'Unbekannter Fehler', variante: 'gefahr' });
      }
      await lade();
    } finally {
      setBeschaeftigt(null);
    }
  };

  if (!geladen || (umsaetze.length === 0 && !ladeFehler)) return null;

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: '8px', border: '1px solid #FDE68A',
      padding: '20px', marginBottom: '20px'
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '4px' }}>
        Zahlungseingänge zur Klärung ({umsaetze.length})
      </div>
      <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px', lineHeight: '1.5' }}>
        Diese Eingänge aus dem Bankabruf ließen sich keiner Rechnung automatisch zuordnen. Passende Rechnung
        auswählen und zuordnen, oder den Eingang ignorieren, wenn er zu keiner Rechnung gehört.
      </div>

      {ladeFehler && (
        <div style={{ padding: '10px 14px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '6px', fontSize: '13px', color: '#991B1B' }}>
          {ladeFehler}
        </div>
      )}

      <div style={{ border: '1px solid #E5E7EB', borderRadius: '6px', overflow: 'hidden' }}>
        {umsaetze.map(u => {
          const optionen = optionenFuer(u);
          const gewaehlt = auswahl[u.id] || '';
          const laeuft = beschaeftigt === u.id;
          return (
            <div key={u.id} style={{
              display: 'grid', gridTemplateColumns: '90px 110px 1fr 300px 190px',
              gap: '12px', padding: '12px 14px', borderBottom: '1px solid #F3F4F6',
              fontSize: '13px', color: '#111827', alignItems: 'center'
            }}>
              <div>{formatDate(u.buchungstag)}</div>
              <div style={{ textAlign: 'right', fontWeight: '600' }}>{formatCurrency(u.betrag)}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.auftraggeber_name || 'Auftraggeber unbekannt'}
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280', wordBreak: 'break-word' }}>
                  {u.verwendungszweck || u.zusatzinfo || 'ohne Verwendungszweck'}
                </div>
              </div>
              <select
                value={gewaehlt}
                onChange={(e) => setAuswahl(prev => ({ ...prev, [u.id]: e.target.value }))}
                disabled={laeuft}
                style={{ padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', width: '100%' }}
              >
                <option value="">Rechnung auswählen ...</option>
                {optionen.map(r => (
                  <option key={r.id} value={r.id}>
                    {gleicherBetrag(r.brutto_betrag, u.betrag) ? '= ' : ''}{r.rechnungsnummer} · {r.empfaenger_firma} · {formatCurrency(r.brutto_betrag)}
                  </option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => handleZuordnen(u)}
                  disabled={laeuft || !gewaehlt}
                  style={{
                    padding: '6px 10px', backgroundColor: laeuft || !gewaehlt ? '#F3F4F6' : '#ECFDF5',
                    color: laeuft || !gewaehlt ? '#9CA3AF' : '#059669',
                    border: `1px solid ${laeuft || !gewaehlt ? '#E5E7EB' : '#A7F3D0'}`, borderRadius: '6px',
                    cursor: laeuft || !gewaehlt ? 'not-allowed' : 'pointer',
                    fontSize: '13px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px'
                  }}
                >
                  {laeuft ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Link2 size={13} />} Zuordnen
                </button>
                <button
                  onClick={() => handleIgnorieren(u)}
                  disabled={laeuft}
                  title="Keiner Rechnung zuordnen"
                  style={{
                    padding: '6px 10px', backgroundColor: '#F9FAFB', color: '#6B7280',
                    border: '1px solid #E5E7EB', borderRadius: '6px',
                    cursor: laeuft ? 'not-allowed' : 'pointer',
                    fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px'
                  }}
                >
                  <EyeOff size={13} /> Ignorieren
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
